
import React, { useState } from 'react';
import { MemberModel, StudentExamModel, SiteSettingModel, EXAM_TYPES } from '../../../../models';
import { db } from '../../../../firebase';
import { doc, updateDoc, collection, addDoc } from 'firebase/firestore';
import { ChevronLeft, Save, Plus, X } from 'lucide-react';

interface StudentExamFormProps {
    member: MemberModel;
    siteSettings: SiteSettingModel;
    exam?: StudentExamModel;
    onBack: () => void;
    onSave: () => void;
}

const StudentExamForm: React.FC<StudentExamFormProps> = ({ member, siteSettings, exam, onBack, onSave }) => {
    const [title, setTitle] = useState(exam?.title || '');
    const [examType, setExamType] = useState(exam?.examType || '');
    const [date, setDate] = useState(exam?.date
        ? new Date(exam.date).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0]);
    const [scores, setScores] = useState<Record<string, number>>(exam?.scores || {});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleScoreChange = (subject: string, value: string) => {
        const num = parseInt(value) || 0;
        setScores({ ...scores, [subject]: Math.min(100, Math.max(0, num)) });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!title.trim()) return alert('시험 제목을 입력해주세요.');

        setIsSubmitting(true);
        try {
            const examData = {
                memberId: member.id,
                branchId: member.branchId,
                title,
                examType,
                date: new Date(date).getTime(),
                scores,
                createdAt: exam?.createdAt || Date.now()
            };

            if (exam) {
                await updateDoc(doc(db, 'studentExams', exam.id), examData as any);
            } else {
                await addDoc(collection(db, 'studentExams'), examData);
            }
            onSave();
        } catch (error) {
            console.error("Error saving exam:", error);
            alert("시험 기록 저장 중 오류가 발생했습니다.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-white rounded-[3rem] border border-slate-100 shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
            <header className="px-8 py-6 border-b border-slate-50 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button onClick={onBack} className="w-10 h-10 flex items-center justify-center bg-slate-50 text-slate-400 rounded-xl hover:bg-slate-100 transition-all">
                        <ChevronLeft size={20} />
                    </button>
                    <h3 className="text-xl font-black text-slate-800">{exam ? '시험 기록 수정' : '새 시험 기록 등록'}</h3>
                </div>
            </header>

            <form onSubmit={handleSubmit} className="p-8 space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">시험 명칭</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="예: 2024년 1학기 중간고사"
                            className="w-full bg-slate-50 border-none rounded-2xl px-6 py-4 text-sm font-bold outline-none ring-1 ring-transparent focus:ring-indigo-100 transition-all"
                        />
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">시험 일자</label>
                        <input
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            className="w-full bg-slate-50 border-none rounded-2xl px-6 py-4 text-sm font-bold outline-none ring-1 ring-transparent focus:ring-indigo-100 transition-all"
                        />
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">시험 구분</label>
                        <select
                            value={examType}
                            onChange={(e) => setExamType(e.target.value)}
                            className="w-full bg-slate-50 border-none rounded-2xl px-6 py-4 text-sm font-bold outline-none ring-1 ring-transparent focus:ring-indigo-100 transition-all appearance-none cursor-pointer"
                        >
                            <option value="">선택 안함</option>
                            {(siteSettings.examTypes && siteSettings.examTypes.length > 0 ? siteSettings.examTypes : EXAM_TYPES).map(type => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="flex items-center justify-between px-1">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">과목별 성적 (0-100)</label>
                        <span className="text-[10px] font-bold text-indigo-500">과목을 선택하여 추가하세요</span>
                    </div>

                    {/* Subject Selector (Available Subjects) */}
                    <div className="flex flex-wrap gap-2 mb-6">
                        {siteSettings.subjects
                            .filter(sub => !Object.keys(scores).includes(sub))
                            .map(sub => (
                                <button
                                    key={sub}
                                    type="button"
                                    onClick={() => setScores({ ...scores, [sub]: 0 })}
                                    className="px-4 py-2 bg-white border border-slate-100 rounded-xl text-xs font-bold text-slate-500 hover:border-indigo-200 hover:text-indigo-600 transition-all flex items-center gap-2"
                                >
                                    <Plus size={14} />
                                    {sub}
                                </button>
                            ))}
                    </div>

                    {/* Added Subject Score Inputs */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {Object.keys(scores).map((sub) => (
                            <div key={sub} className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-2 relative group">
                                <button
                                    type="button"
                                    onClick={() => {
                                        const newScores = { ...scores };
                                        delete newScores[sub];
                                        setScores(newScores);
                                    }}
                                    className="absolute -top-2 -right-2 w-6 h-6 bg-rose-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                                >
                                    <X size={12} />
                                </button>
                                <span className="text-[10px] font-black text-slate-500">{sub}</span>
                                <input
                                    type="number"
                                    value={scores[sub] || ''}
                                    onChange={(e) => handleScoreChange(sub, e.target.value)}
                                    placeholder="0"
                                    className="w-full bg-white border-none rounded-xl px-3 py-2 text-sm font-black text-indigo-600 outline-none ring-1 ring-slate-100 focus:ring-indigo-100"
                                />
                            </div>
                        ))}
                        {Object.keys(scores).length === 0 && (
                            <div className="col-span-full py-10 text-center bg-slate-50/50 rounded-3xl border-2 border-dashed border-slate-100">
                                <p className="text-xs font-bold text-slate-400">아직 선택된 과목이 없습니다. 위에서 선택해 주세요!</p>
                            </div>
                        )}
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-5 bg-indigo-600 text-white rounded-[2rem] text-sm font-black shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                    {isSubmitting ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                        <>
                            <Save size={18} />
                            {exam ? '기록 수정하기' : '금회 시험 저장하기'}
                        </>
                    )}
                </button>
            </form>
        </div>
    );
};

export default StudentExamForm;
